import { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { ChevronLeft } from 'lucide-react';
import { C } from '../constants';
import Glass from '../components/Glass';
import { supabase } from '../supabase';
import { setMeta, setJsonLd, removeJsonLd } from '../utils/meta';

const CATEGORY_COLORS = {
  analysis: { bg: 'rgba(0,229,255,0.12)', text: C.cyan,  label: 'Analysis' },
  guide:    { bg: 'rgba(120,90,255,0.12)', text: '#A78BFA', label: 'Guide' },
  story:    { bg: 'rgba(255,77,158,0.12)', text: C.pink,  label: 'Story' },
};

function ContentBody({ content }) {
  if (!content) return null;
  const { intro, sections = [], closing } = content;

  return (
    <div style={{ maxWidth: 720 }}>
      {/* Intro */}
      {intro && (
        <p style={{
          fontSize: '1.05rem', lineHeight: 1.75, color: 'rgba(255,255,255,0.82)',
          marginBottom: '2rem', fontWeight: 400,
        }}>
          {intro}
        </p>
      )}

      {/* Sections */}
      {sections.map((sec, i) => (
        <div key={i} style={{ marginBottom: '2rem' }}>
          <h2 style={{
            fontSize: '1.2rem', fontWeight: 700, color: C.white,
            letterSpacing: '-0.01em', lineHeight: 1.3,
            marginBottom: '0.75rem',
          }}>
            {sec.heading}
          </h2>
          <p style={{
            fontSize: '0.95rem', lineHeight: 1.75,
            color: 'rgba(255,255,255,0.75)', margin: 0,
          }}>
            {sec.body}
          </p>
          {sec.bullets && sec.bullets.length > 0 && (
            <ul style={{ marginTop: '0.75rem', paddingLeft: '1.25rem', display: 'flex', flexDirection: 'column', gap: '0.4rem' }}>
              {sec.bullets.map((b, j) => (
                <li key={j} style={{ fontSize: '0.93rem', color: 'rgba(255,255,255,0.72)', lineHeight: 1.6 }}>
                  {b}
                </li>
              ))}
            </ul>
          )}
        </div>
      ))}

      {/* Closing */}
      {closing && (
        <Glass style={{ padding: '1.1rem 1.3rem', marginTop: '1rem' }}>
          <p style={{ fontSize: '0.95rem', lineHeight: 1.65, color: C.white, margin: 0, fontStyle: 'italic' }}>
            {closing}
          </p>
        </Glass>
      )}
    </div>
  );
}

export default function BlogPostScreen() {
  const { slug }    = useParams();
  const navigate    = useNavigate();
  const [post, setPost]     = useState(null);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);

  useEffect(() => {
    // Clean up BlogPosting schema on unmount / slug change
    return () => removeJsonLd('blog-post');
  }, [slug]);

  useEffect(() => {
    supabase
      .from('blogs')
      .select('*')
      .eq('slug', slug)
      .maybeSingle()
      .then(({ data }) => {
        if (data) {
          setPost(data);

          const postUrl = `https://unofficialelections.com/blog/${data.slug}`;
          const ogImage = `https://unofficialelections.com/og-blog-${data.slug}.png`;
          const published = new Date(data.published_at).toISOString();

          setMeta({
            title:         `${data.meta_title || data.title} | UnofficialElections Blog`,
            description:   data.meta_desc || data.excerpt,
            ogTitle:       data.title,
            ogDescription: data.excerpt,
            ogImage:       `/og-blog-${data.slug}.png`,
            ogType:        'article',
            canonical:     `/blog/${data.slug}`,
          });

          // BlogPosting structured data
          setJsonLd('blog-post', {
            '@context':          'https://schema.org',
            '@type':             'BlogPosting',
            headline:            data.title,
            description:         data.meta_desc || data.excerpt,
            image:               ogImage,
            url:                 postUrl,
            datePublished:       published,
            dateModified:        published,
            keywords:            (data.tags || []).join(', '),
            timeRequired:        `PT${data.reading_mins}M`,
            inLanguage:          'en-IN',
            author: {
              '@type': 'Organization',
              name:    'UnofficialElections',
              url:     'https://unofficialelections.com',
            },
            publisher: {
              '@type': 'Organization',
              name:    'UnofficialElections',
              url:     'https://unofficialelections.com',
              logo: {
                '@type': 'ImageObject',
                url:     'https://unofficialelections.com/ue-logo.jpg',
              },
            },
            mainEntityOfPage: {
              '@type': 'WebPage',
              '@id':   postUrl,
            },
          });
        } else setNotFound(true);
        setLoading(false);
      });
  }, [slug]);

  if (loading) {
    return (
      <main className="px-5 md:px-10 py-20 max-w-4xl mx-auto" style={{ textAlign: 'center' }}>
        <span style={{ color: C.textDim }}>Loading…</span>
      </main>
    );
  }

  if (notFound) {
    return (
      <main className="px-5 md:px-10 py-20 max-w-4xl mx-auto" style={{ textAlign: 'center' }}>
        <p style={{ color: C.textDim }}>Post not found.</p>
        <button onClick={() => navigate('/blog')} style={{ color: C.cyan, background: 'none', border: 'none', cursor: 'pointer', marginTop: '1rem', fontSize: '0.9rem' }}>
          ← Back to blog
        </button>
      </main>
    );
  }

  const cat = CATEGORY_COLORS[post.category] || CATEGORY_COLORS.guide;
  const accent = C.cyan;

  return (
    <main className="px-5 md:px-10 py-10 max-w-4xl mx-auto">
      {/* Back */}
      <button
        onClick={() => navigate('/blog')}
        className="inline-flex items-center gap-1.5"
        style={{
          background: 'none', border: 'none', color: C.textDim, cursor: 'pointer',
          fontSize: '0.85rem', fontFamily: "'Poppins', sans-serif",
          marginBottom: '2rem', padding: 0,
        }}
        onMouseEnter={e => e.currentTarget.style.color = C.white}
        onMouseLeave={e => e.currentTarget.style.color = C.textDim}
      >
        <ChevronLeft size={15} /> Back to blog
      </button>

      {/* Meta row */}
      <div className="flex items-center gap-3 flex-wrap" style={{ marginBottom: '1rem' }}>
        <span style={{
          fontSize: '0.65rem', fontWeight: 700, letterSpacing: '0.12em',
          textTransform: 'uppercase', padding: '3px 10px', borderRadius: 999,
          background: cat.bg, color: cat.text,
        }}>
          {cat.label}
        </span>
        <span style={{ fontSize: '0.72rem', color: C.textDim }}>
          {post.reading_mins} min read
        </span>
        <span style={{ fontSize: '0.72rem', color: C.textDimmer }}>·</span>
        <span style={{ fontSize: '0.72rem', color: C.textDimmer }}>
          {new Date(post.published_at).toLocaleDateString('en-IN', { day: 'numeric', month: 'long', year: 'numeric' })}
        </span>
      </div>

      {/* Title */}
      <h1 style={{
        fontWeight: 700, fontSize: 'clamp(1.6rem, 4vw, 2.6rem)',
        lineHeight: 1.1, letterSpacing: '-0.02em',
        color: C.white, marginBottom: '1rem',
      }}>
        {post.title}
      </h1>

      {/* Excerpt */}
      <p style={{
        fontSize: '1rem', color: C.textDim, lineHeight: 1.6,
        marginBottom: '2.5rem', maxWidth: 620,
        borderLeft: `3px solid ${accent}`,
        paddingLeft: '1rem',
      }}>
        {post.excerpt}
      </p>

      {/* Divider */}
      <div style={{ borderTop: `1px solid ${C.border}`, marginBottom: '2.5rem' }} />

      {/* Content */}
      <ContentBody content={post.content} />

      {/* Tags */}
      <div className="flex gap-2 flex-wrap" style={{ marginTop: '3rem', paddingTop: '1.5rem', borderTop: `1px solid ${C.border}` }}>
        {post.tags.map(t => (
          <span key={t} style={{
            fontSize: '0.7rem', padding: '4px 10px', borderRadius: 999,
            background: 'rgba(255,255,255,0.05)', color: C.textDim,
            border: `1px solid ${C.border}`,
          }}>
            {t}
          </span>
        ))}
      </div>
    </main>
  );
}
